import {Api, Method} from "../../api/api";
import {renderElement} from "../../utils/common";
import {ActionUrl, TAG_CLASS_NAME, TAG_LIST_CLASS_NAME, TaskType} from "../constants";
import {getTagsTemplate, getTaskTagTemplate} from "../template";
import {showToast} from "../../toast";
import {tagsEventEmitter} from "../event-emitter";

/**
 * Функционал отвечающий за добавление меток в задачу
 */
class AddTag {
  #button;
  #parent;
  #popupElement;
  #tagsWrapperElement;
  #hiddenInputElement;

  #api = new Api();
  #url = ActionUrl.ADD_TAG_TO_TASK_URL;
  #type = null;
  #idValues;
  #emitter = tagsEventEmitter;

  constructor(button) {
    this.#button = button;

    this.#init();
  }

  #init() {
    this.#parent = this.#button.closest(`.js-add-tag-parent`);
    this.#popupElement = this.#parent.querySelector(`.js-tags-modal`);
    this.#tagsWrapperElement = this.#parent.querySelector(`.${TAG_LIST_CLASS_NAME}`);
    this.#hiddenInputElement = this.#parent.querySelector(`.js-tags-h-input`);
    if (this.#hiddenInputElement) {
      this.#idValues = new Set(JSON.parse(this.#hiddenInputElement.value));
    }

    this.onPopupElementClick = this.onPopupElementClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onRemoveTag = this.onRemoveTag.bind(this);
    this.onProjectChange = this.onProjectChange.bind(this);
    this.onBtnElementClick = this.onBtnElementClick.bind(this);

    this.#emitter.on(`removeTaskTag`, this.onRemoveTag);
    this.#emitter.on(`projectChanged`, this.onProjectChange);
    this.#button.addEventListener(`click`, this.onBtnElementClick);
  }

  #hidePopup() {
    this.#popupElement.classList.remove(`modal--show`);

    this.#popupElement.removeEventListener(`click`, this.onPopupElementClick);
    document.removeEventListener(`click`, this.onDocumentClick);
  }

  #showPopup() {
    this.#popupElement.classList.add(`modal--show`);

    this.#popupElement.addEventListener(`click`, this.onPopupElementClick);
    document.addEventListener(`click`, this.onDocumentClick);
  }

  #getTagData(tag) {
    return {
      id: +tag.dataset.id,
      taskId: +tag.dataset.taskId,
      color: tag.dataset.color,
      name: tag.textContent.trim(),
      type: this.#type,
    };
  }

  #renderTag(tagData) {
    if (this.#tagsWrapperElement) {
      renderElement(this.#tagsWrapperElement, getTaskTagTemplate(tagData));
      this.#hidePopup();
    }
  }

  #renderTagsInModal(data) {
    this.#popupElement.innerHTML = ``;
    renderElement(this.#popupElement, getTagsTemplate(data));
  }

  #updateIdValues() {
    this.#hiddenInputElement.value = JSON.stringify(Array.from(this.#idValues));
  }

  #setId(id) {
    this.#idValues.add(id);

    this.#updateIdValues();
  }

  #deleteId(id) {
    this.#idValues.delete(id);

    this.#updateIdValues();
  }

  onRemoveTag(id) {
    this.#deleteId(id);

    const tagElements = Array.from(this.#popupElement.querySelectorAll(`li`));

    tagElements.find((tag) => +tag.dataset.id === id).classList.remove(`tag--active`);
  }

  onProjectChange(id) {
    this.#fetchChangedProject(id);
  }

  async #fetchChangedProject(id) {
    const {code, answer} = await this.#api.customQuery(ActionUrl.CHANGE_PROJECT_URL, {
      method: Method.POST,
      query: JSON.stringify({id}),
    });

    if (code === 200) {
      this.#tagsWrapperElement.innerHTML = ``;
      this.#hiddenInputElement.value = `[]`;
      this.#idValues.clear();
      this.#renderTagsInModal(answer.data);
    } else {
      showToast(answer.text);
    }
  }

  async #fetchTagToServer(tag) {
    const {code, answer} = await this.#api.customQuery(this.#url, {
      method: Method.POST,
      query: JSON.stringify({id: +tag.dataset.id, taskId: +tag.dataset.taskId}),
    });

    if (code === 200) {
      this.#renderTag(this.#getTagData(tag));
    } else {
      showToast(answer.text);
    }
  }

  onPopupElementClick({target}) {
    if (target.classList.contains(TAG_CLASS_NAME)) {
      this.#type = target.dataset.type;

      if (this.#type === TaskType.ADD) {
        this.#fetchTagToServer(target);
      } else {
        if (!this.#idValues.has(+target.dataset.id)) {
          this.#setId(+target.dataset.id);
          target.classList.add(`tag--active`);
          this.#renderTag(this.#getTagData(target));
        } else {
          this.#hidePopup();
        }
      }
    }
  }

  onDocumentClick({target}) {
    if (!target.closest(`.js-tags-modal`) && target !== this.#button) {
      this.#hidePopup();
    }
  }

  onBtnElementClick() {
    this.#showPopup();
  }
}

export {AddTag};
